import {
  apiGetTicket,
  apiLoginTest,
  apiLoginGoogle,
  apiLoginApple
} from '@/api/login'
import { apiUpdateLanguage } from '@/api/user'
import type { loginInterface } from '@/types/apiType/loginType'

export const useLoginStore = defineStore({
  id: 'login',
  state: () => ({
    isShowLogin: false, // 是否显示登录弹窗
    isLazyShowLogin: false,
    loginBtnLogging: {
      // 登录按钮状态
      isGoogle: false, // 谷歌
      isApple: false // 苹果
    },
    visitorId: '', // 设备ID
    utmCountry: '' as string, //  广告系列 国家 存投放进来的国家，和用户选择的国家有区别
    utmCampaign: '' as string // 广告系列 ID 买量送免费券做用
  }),
  actions: {
    // 设置广告系列ID
    setUTMCampaign(id: string) {
      this.utmCampaign = id
    },
    // 设置默认国家
    setUTMCountry(country) {
      this.utmCountry = country
    },
    // 设置是否显示登录
    setIsShowLogin(value: boolean) {
      this.isLazyShowLogin = true
      this.isShowLogin = value
    },
    // 获取设备号
    async getVisitorId() {
      if (this.visitorId) return this.visitorId
      const result = await useNuxtApp().$agent.get()
      this.visitorId = result.visitorId
      return this.visitorId
    },
    // 设置苹果登录按钮状态
    setLoginBtnLoggingApple(loading: boolean) {
      this.loginBtnLogging.isApple = loading
    },
    // 设置谷歌登录按钮状态
    setLoginBtnLoggingGoogle(loading: boolean) {
      this.loginBtnLogging.isGoogle = loading
    },
    // 测试登录
    async testLogin(unionid: number) {
      await this.getVisitorId()
      const res = await apiLoginTest({
        unionid: unionid as number,
        country: this.utmCountry,
        gclid: this.utmCampaign,
        deviceId: this.visitorId,
        app: 'xchat'
      })
      const { code, data } = res
      if (code === 200 && data) {
        localStorage.setItem('loginInfo', JSON.stringify(data))
        this.loginProcess(data, 2)
      }
    },
    // 谷歌重定向登录
    redirectGoogleLogin() {
      const runtimeConfig = useRuntimeConfig()
      this.setLoginBtnLoggingGoogle(true)

      // 延迟保证utmCampaignSearch参数保存
      setTimeout(() => {
        // Google's OAuth 2.0 endpoint for requesting an access token
        const oauth2Endpoint = 'https://accounts.google.com/o/oauth2/v2/auth'

        // Create <form> element to submit parameters to OAuth 2.0 endpoint.
        const form = document.createElement('form')
        form.setAttribute('method', 'GET') // Send as a GET request.
        form.setAttribute('action', oauth2Endpoint)

        // Parameters to pass to OAuth 2.0 endpoint.
        const params: any = {
          client_id: runtimeConfig.public.googleClientId,
          redirect_uri: runtimeConfig.public.googleLoginRedirectUrl,
          response_type: 'token',
          scope:
            'openid https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email',
          include_granted_scopes: 'true',
          state: 'pass-through value'
        }

        // Add form parameters as hidden input values.
        for (const p in params) {
          const input = document.createElement('input')
          input.setAttribute('type', 'hidden')
          input.setAttribute('name', p)
          input.setAttribute('value', params[p])
          form.appendChild(input)
        }

        // Add form to page and submit it to open the OAuth 2.0 endpoint.
        document.body.appendChild(form)
        form.submit()
      }, 240)
    },
    // 谷歌重定向回调登录
    async googleLoign(loginData) {
      this.setLoginBtnLoggingGoogle(true)
      await this.getVisitorId()
      const res = await apiLoginGoogle({
        idToken: loginData.access_token,
        displayName: '',
        photoURL: '',
        deviceId: this.visitorId,
        country: this.utmCountry,
        gclid: this.utmCampaign,
        app: 'xchat'
      })
      const { code, data } = res
      if (code === 200 && data) {
        localStorage.setItem('loginInfo', JSON.stringify(data))
        return this.loginProcess(data, 1)
      } else {
        this.setLoginBtnLoggingGoogle(false)
      }
    },
    // 苹果登录
    async appleLogin() {
      try {
        this.setLoginBtnLoggingApple(true)
        const data = await apple.signIn()
        const {
          authorization: { id_token: token, code },
          user
        } = data
        if (token && code) {
          await this.getVisitorId()
          const res = await apiLoginApple({
            idToken: token,
            displayName: user?.name.firstName || '',
            photoURL: '',
            deviceId: this.visitorId,
            country: this.utmCountry,
            gclid: this.utmCampaign,
            app: 'xchat'
          })
          const { code, data } = res
          if (code === 200 && data) {
            localStorage.setItem('loginInfo', JSON.stringify(data))
            return this.loginProcess(data, 3)
          } else {
            this.setLoginBtnLoggingApple(false)
          }
        }
      } catch (error) {
        console.log('apple fun error:', error)
        this.setLoginBtnLoggingApple(false)
      }
    },
    /* 登录最后调用函数 
    登录流程  type: 1：谷歌 2: 测试环境 3: Apple
    */
    async loginProcess(data: loginInterface, type: 1 | 2 | 3) {
      const publicStore = usePublicStore()
      const callStore = useCallStore()
      const rechargeStore = useRechargeStore()
      const socketStore = useSocketStore()
      const chatStore = useChatStore()
      const userStore = useUserStore()
      // const languageStore = useLanguageStore()

      try {
        this.setUTMCampaign('') // 接口完成就清空
        this.setUTMCountry('') // 接口完成就重置

        // 获取ticket信息
        const ticketRes = await apiGetTicket({
          access_token: data.access_token
        })
        const { code: ticketCode, data: ticketData } = ticketRes
        if (ticketCode === 200) {
          localStorage.setItem('ticket', ticketData.tickets[0].ticket)
        } else {
          this.setLoginBtnLoggingApple(false)
          this.setLoginBtnLoggingGoogle(false)
        }

        // 埋点
        eventTracking.setUid(data.uid)
        switch (type) {
          case 1:
            eventTracking.eventLog(eventTracking.eventId.login_way_google) // 谷歌登录埋点
            eventTracking.eventLog(
              eventTracking.eventIdFB.CompleteRegistration,
              {
                userId: data.uid
              }
            ) // FB登录
            eventTracking.eventLog(
              data.register
                ? eventTracking.eventId.new_user_registration_google
                : eventTracking.eventId.login_historical_user_google
            ) // 新用户 : 老用户
            break
          case 3:
            eventTracking.eventLog(eventTracking.eventId.login_way_apple) // 苹果登录埋点
            eventTracking.eventLog(
              eventTracking.eventIdFB.CompleteRegistration,
              {
                userId: data.uid
              }
            ) // FB新注册事件

            eventTracking.eventLog(
              data.register
                ? eventTracking.eventId.new_user_registration_apple
                : eventTracking.eventId.login_historical_user_apple
            ) // 新用户 : 老用户
            break
        }

        // 新用户或 （language没值没字段）的情况，先配置第一语言
        if (data.register || !data.language) {
          await apiUpdateLanguage({
            // updateLanguage: languageStore.languageInfo.text, 暂时默认英语
            updateLanguage: 'English',
            type: 1
          })
        }

        // 获取用户信息
        const isLogin = await userStore.requestUserInfo({ avatarLoad: true })

        // 调取接口登录成功
        if (isLogin) {
          userStore.getLevelExperienceInfo() // 获取财富等级

          this.setIsShowLogin(false) // 关闭登录窗口

          userStore.changeManOnline(1) // 发上线状态

          callStore.getMatchPoolDetail() // 更新匹配免费券

          // 判断是否有免费券，有就显示弹窗
          if (data.freeCard > 0) {
            publicStore.setShowFreeCardPopup(true)
          }

          // 查询充值订单
          rechargeStore.setUid()
          rechargeStore.requestRechargeResult()

          // socket初始化
          socketStore.init()
          // 连接云信
          chatStore.initNIM()
          // 重置状态
          this.setLoginBtnLoggingApple(false)
          this.setLoginBtnLoggingGoogle(false)
        }
      } catch (error) {
        console.log('login end fun error:', error)
        // 重置状态
        this.setLoginBtnLoggingApple(false)
        this.setLoginBtnLoggingGoogle(false)
      }
    },
    /**
     * 初始化登录，用做进入页面就调用判断
     */
    async initLogin() {
      const userStore = useUserStore()
      const publicStore = usePublicStore()
      const socketStore = useSocketStore()
      const callStore = useCallStore()
      const rechargeStore = useRechargeStore()
      const chatStore = useChatStore()
      // 检查 ticket&login 获取用户信息
      if (localStorage.getItem('ticket') && localStorage.getItem('loginInfo')) {
        this.setLoginBtnLoggingGoogle(true)
        await userStore.requestUserInfo({ avatarLoad: true })
        eventTracking.setUid(userStore.userInfo.uid) // 设置gtagUid
        userStore.getLevelExperienceInfo()
        userStore.changeManOnline(1)
        callStore.getMatchPoolDetail()
        socketStore.init()
        publicStore.setIsShowView()
        // 查询充值订单
        rechargeStore.setUid()
        rechargeStore.requestRechargeResult()
        // 连接云信
        chatStore.initNIM()

        this.setLoginBtnLoggingGoogle(false)
        console.log('App.vue-inspectTicket-登录成功')
      } else {
        publicStore.resetLogin({
          msg: '登录初始化 !(ticket&&loginInfo)',
          type: 'init',
          direction: false
        })
        publicStore.setIsShowView()
      }
    }
  },
  persist: {
    key: 'loginStore',
    paths: ['utmCountry', 'utmCampaign']
  }
})
